import {
  type BlobUploadCommonResponse,
  BlockBlobClient
} from '@azure/storage-blob'
import {
  generateProfileImageUploadTicketRequest,
  processProfileImageUploadTicketRequest
} from './profile-image-api-calls'

export type UploadProfileImageResponse = {
  processedImageUploadId: string
  processedImagePreviewUrl: string
}

export async function uploadProfileImage(
  file: File,
  uploaderUserId: string,
  commandId: string
): Promise<UploadProfileImageResponse> {
  if (!file) {
    throw new Error('No file selected.')
  }
  const generateProfileImageUploadTicketResult =
    await generateProfileImageUploadTicketRequest({
      commandId
    })

  const { uploadTicketId, uploadUrl } = generateProfileImageUploadTicketResult
  const [uploadError, uploadResult] = await uploadToBlobStorage(
    file,
    uploadUrl,
    {
      commandId: commandId,
      purpose: 'user-profile-image',
      uploaderUserId: uploaderUserId
    }
  )
  if (uploadError || !uploadResult) {
    throw new Error(uploadError ?? 'Failed to upload profile image.')
  }

  const processProfileImageUploadTicketResult =
    await processProfileImageUploadTicketRequest({
      commandId: commandId,
      ticketId: uploadTicketId
    })

  return processProfileImageUploadTicketResult
}

export type UploadResponse =
  | [string, undefined]
  | [undefined, BlobUploadCommonResponse]

async function uploadToBlobStorage(
  data: File,
  sasUrl: string,
  metadata: {
    commandId: string
    purpose: 'user-profile-image'
    uploaderUserId: string
  }
): Promise<UploadResponse> {
  if (!sasUrl) {
    return ['Please enter your container SAS URL.', undefined]
  }
  if (!data) {
    return ['Please select a file.', undefined]
  }
  try {
    const blockBlobClient = new BlockBlobClient(sasUrl)

    const response = await blockBlobClient.uploadData(data, {
      blobHTTPHeaders: {
        blobContentType: data.type
      },
      metadata: {
        commandId: metadata.commandId,
        purpose: metadata.purpose,
        uploaderUserId: metadata.uploaderUserId
      }
    })
    return [undefined, response]
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    return ['Upload failed: ' + message, undefined]
  }
}
